import React from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import { media } from "../theme/breakpoints";
import EmptyState from "../components/EmptyState";
import Badge from "../components/Badge";
import theme from "../../../assets/theme";

const TodaySchedule = ({ bookings = [], onOpenBooking }) => {
  const today = new Date().toISOString().split("T")[0];

  const todayBookings = bookings
    .filter(
      (item) =>
        item.hearing_date === today &&
        ["confirmed", "reserved"].includes(item.booking_status),
    )
    .sort((a, b) => a.start_time.localeCompare(b.start_time));

  const formatTime = (time) => {
    if (!time) return "--";

    const [hour, minute] = time.split(":");
    const h = Number(hour);
    const suffix = h >= 12 ? "PM" : "AM";
    const formattedHour = h % 12 || 12;

    return `${formattedHour}:${minute} ${suffix}`;
  };

  return (
    <Card $padding="0" style={{ overflow: "hidden", borderRadius: "20px" }}>
      <Header>
        <HeaderLeft>
          <HeaderTitle>Today's Schedule</HeaderTitle>

          {todayBookings.length > 0 && (
            <CountPill>{todayBookings.length} sessions</CountPill>
          )}
        </HeaderLeft>

        <HeaderDate>
          {new Date().toLocaleDateString("en-IN", {
            weekday: "long",
            month: "short",
            day: "numeric",
          })}
        </HeaderDate>
      </Header>

      <ListWrap $hasItems={todayBookings.length > 0}>
        {todayBookings.length === 0 ? (
          <EmptyStateWrapper>
            <EmptyState
              icon="🕒"
              title="No bookings today"
              subtitle="No room bookings scheduled for today."
            />
          </EmptyStateWrapper>
        ) : (
          todayBookings.map((booking) => (
            <BookingRow
              key={booking.id}
              onClick={() => onOpenBooking?.(booking)}
            >
              <TimeBlock>
                <TimeStart>{formatTime(booking.start_time)}</TimeStart>
                <TimeDuration>{booking.session_duration}</TimeDuration>
              </TimeBlock>

              <RoomInfo>
                <RoomName>{booking.hearing_room}</RoomName>

                <Parties>
                  {booking.claimants_list?.[0]?.Name || "—"} vs{" "}
                  {booking.respondents_list?.[0]?.Name || "—"}
                </Parties>
              </RoomInfo>

              <RefBadge>
                <Badge color="navy" size="sm">
                  {booking.booking_reference}
                </Badge>
              </RefBadge>
            </BookingRow>
          ))
        )}
      </ListWrap>
    </Card>
  );
};

export default TodaySchedule;

const Card = styled.div`
  background: ${COLORS.PRIMARY_WHITE};
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 10px;
  padding: ${({ $padding }) =>
    $padding !== undefined ? `${$padding}px` : "22px"};
`;

const Header = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${COLORS.BORDER_GREY};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(to right, #f8fafc, #f1f5f9);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderTitle = styled.h3`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 17px;
  font-weight: 800;
  color: ${COLORS.COLOR_DARK};
  margin: 0;
`;

const CountPill = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  font-weight: 700;
  background: rgba(16, 185, 129, 0.12);
  color: ${COLORS.BTN_GREEN};
  padding: 4px 10px;
  border-radius: 9999px;
`;

const HeaderDate = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13.5px;
  color: ${COLORS.INPUT_LABEL};
`;

const ListWrap = styled.div`
  padding: ${({ $hasItems }) => ($hasItems ? "6px 0" : "40px 0")};
`;

const EmptyStateWrapper = styled.div`
  padding: 60px 20px;
`;

const BookingRow = styled.div`
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid ${COLORS.BORDER_GREY};
  position: relative;

  &:hover {
    background: #f8fafc;
    transform: translateX(4px);
  }

  &:last-child {
    border-bottom: none;
  }

  ${media.tablet} {
    padding: 18px 28px;
    gap: 24px;
  }
`;

const TimeBlock = styled.div`
  min-width: 68px;
  text-align: center;
  flex-shrink: 0;
`;

const TimeStart = styled.div`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 16px;
  color: ${COLORS.COLOR_DARK};
  line-height: 1;
`;

const TimeDuration = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
`;

const RoomInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const RoomName = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  margin-bottom: 3px;
`;

const Parties = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13.5px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RefBadge = styled.div`
  flex-shrink: 0;
  align-self: center;
`;
